import _, { cloneDeep, unset } from "lodash";

import AppConstants from "../constants/AppConstants";
import PropertyMaps from "../constants/PropertyMaps";
import { gatherProps, getProp } from "@/utils/Merge/MergeUtils.mjs";

import { TEXT_STYLE } from "../constants/MergeConstants";
import { isSynced } from "../utils/SyncUtils";
import { LAYOUTS, LAYOUT_MOBILE } from "../constants/DesignConstants.mjs";
import { ELEMENTS } from "../constants/EntityConstants";

const {
  TEXT_STYLE_PROPS: ELEMENT_TEXT_STYLE_PROPS,
  STYLE_PROPS_THEME_BUTTONS,
  STYLE_PROPS_BUTTON_TEXT,
  TEXT_NESTED_STYLES_INLINE_LINK,
  ELEMENT_POSITION,
  ELEMENT_TRANSITION_PROPS,
  PROP_LOCKING_H,
  PROP_LOCKING_V,
} = PropertyMaps;

let STYLE_LAYOUT_COLOR_PROPS = ["hC", "c", "link.c", "link.hC"];

let BACKGROUND_COLOR_PROPS = ["background.color", "bgColor", "bgGc1", "bgGc2"];

let ELEMENT_COLOR_PROPS = [
  "bC",
  "hC",
  "bgcolor",
  "c",
  "gc1",
  "gc2",
  "shadow.sc",
  "border.c",
  "border.a.t.c",
  "border.a.b.c",
  "border.a.l.c",
  "border.a.r.c",
  "gallery.imgBorder.c",
  "gallery.dots.c",
  "gallery.dots.ac",
  "gallery.fs.bgc",
  "gallery.fs.cc",
  "hover.c",
  "hover.bC",
  "hover.bgcolor",
  "hover.gc1",
  "hover.gc2",
  "hover.shadow.sc",
];

const BUTTON_COLOR_PROPS = [...ELEMENT_COLOR_PROPS, ...[
  "border.color",
  "hover.border.color",
  "text.c",
  "hover.text.c",
]];

/**
 * Don't map marginBottom 'mB' because there is no user control for the user to set it.
 * 'tD' - text decoration, not used currently
 * 'hTd' - hover text decoration, not used currently
 */
const TEXT_STYLE_PROPS = ["size", "lH", "lS", "tA", "lC", "c", "font"];

const gatherableElementProps = [
  TEXT_STYLE,
  ...ELEMENT_TEXT_STYLE_PROPS,
  ...TEXT_NESTED_STYLES_INLINE_LINK,
  ...ELEMENT_POSITION,
  ...ELEMENT_TRANSITION_PROPS,
  PROP_LOCKING_H,
  PROP_LOCKING_V,
];

const gatherableButtonProps = [
  ...STYLE_PROPS_THEME_BUTTONS,
  ...STYLE_PROPS_BUTTON_TEXT,
  ...ELEMENT_POSITION,
  ...ELEMENT_TRANSITION_PROPS,
  PROP_LOCKING_H,
  PROP_LOCKING_V,
];

class ThemeApplier {
  constructor(/** Object **/ themeToApply) {
    //copy theme so that it can be modified
    this.theme = _.cloneDeep(themeToApply);
    this.theme = this._applyColorsToStyles(this.theme);

    /** @type {Record<string, string>} */
    this.fontsInUse = {};
  }

  /**
   * Applies theme to item
   * @param itemData - page or block data
   */
  applyTo(/** Object **/ itemData) {
    this.fontsInUse = {};

    let result = {};

    const type = _.get(itemData, "type");
    let item = null;

    switch (type) {
      case AppConstants.SITE_ITEM_PAGE:
        this._applyColors(itemData, BACKGROUND_COLOR_PROPS);
        _.forEach(_.get(itemData, "blockData", {}), _.bind(this._processBlock, this));
        item = itemData;
        break;
      case AppConstants.SITE_ITEM_BLOCK:
        item = this._processBlock(itemData);
        break;
    }

    if (item) {
      result.item = item;
      result.theme = this._determineThemeChanges(this.fontsInUse);
    }

    return result;
  }

  _determineThemeChanges(fontsInUse) {
    let themeChanges = {};

    if (_.size(fontsInUse) > 0) {
      themeChanges.fonts = {};
      _.forEach(
        fontsInUse,
        _.bind(function (fontName) {
          const font = _.get(this.theme, ["fonts", fontName]);
          if (font) {
            _.set(themeChanges.fonts, fontName, _.cloneDeep(font));
          }
        }, this)
      );
    }

    return themeChanges;
  }

  /**
   * @param {Object} layoutData
   * @description This matches any font key at any depth. Unknown fonts are filtered out later in _determineThemeChanges.
   */
  _collectFonts(layoutData) {
    if (!_.isObject(layoutData)) {
      return;
    }

    _.forEach(layoutData, (value, key) => {
      if (key === "font") {
        if (_.isString(value) && value) {
          this.fontsInUse[value] = value;
        }
      } else if (_.isObject(value)) {
        this._collectFonts(value);
      }
    });
  }

  _processBlock(block) {
    this._applyColors(block, BACKGROUND_COLOR_PROPS);

    block.elements = (block?.elements || []).map(this._processElement.bind(this));
    block.states = (block?.states || []).map(this._processState.bind(this));

    return block;
  }

  _processState(state) {
    this._applyColors(state, BACKGROUND_COLOR_PROPS);

    state.elements = (state?.elements || []).map(this._processElement.bind(this));

    return state;
  }

  _processElement(element) {
    element = this._applyColors(element, ELEMENT_COLOR_PROPS);

    const type = _.get(element, "type");

    switch (type) {
      case AppConstants.ELEMENT_TYPE_TEXT:
        element = this._processTextStyle(element);
        break;
      case AppConstants.ELEMENT_TYPE_BUTTON:
        element = this._processButtonStyle(element);
        break;
      case AppConstants.ELEMENT_TYPE_GROUP:
        element = this._processGroup(element);
        break;
    }

    return element;
  }

  /**
   * @param {Record<string, any>} group
   * @description Children may be an array or an id-keyed object, same as DesignPreparer.cleanElements handles them.
   */
  _processGroup(group) {
    const children = _.get(group, ELEMENTS);

    if (_.isEmpty(children)) {
      return group;
    }

    group[ELEMENTS] = _.isArray(children)
      ? children.map(this._processElement.bind(this))
      : _.mapValues(children, this._processElement.bind(this));

    return group;
  }

  _processTextStyle(element) {
    const elem = cloneDeep(element);
    const sync = [...(elem.sync || [])];

    LAYOUTS.forEach((layout) => {
      const style = getProp(elem, layout, "style");

      if (style) {
        const themeStyle = _.get(this.theme, ["styles", style, layout]);
        if (themeStyle) {
          const gatheredProps = gatherProps(elem, layout, gatherableElementProps, true, this.theme);

          if (layout === LAYOUT_MOBILE) {
            gatherableElementProps.forEach((prop) => {
              if (isSynced(element, prop, layout, this.theme)) {
                sync.push(prop);
                unset(gatheredProps, prop);
              }
            });
          }

          elem[layout] = _.merge({}, _.omit(elem[layout], gatherableElementProps), gatheredProps);
        }
      }

      this._collectFonts(elem[layout]);
    });
    elem.sync = Array.from(new Set([...sync]));

    return elem;
  }

  _processButtonStyle(element) {
    const elem = cloneDeep(element);
    const sync = [...(elem.sync || [])];

    LAYOUTS.forEach((layout) => {
      const { style } = elem;

      if (style) {
        const themeStyle = _.get(this.theme, ["styles", style, layout]);
        if (themeStyle) {
          const gatheredProps = gatherProps(elem, layout, gatherableButtonProps, true, this.theme);

          if (layout === LAYOUT_MOBILE) {
            gatherableButtonProps.forEach((prop) => {
              if (isSynced(element, prop, layout, this.theme)) {
                sync.push(prop);
                unset(gatheredProps, prop);
              }
            });
          }

          elem[layout] = _.merge({}, _.omit(elem[layout], gatherableButtonProps), gatheredProps);
        }
      }

      this._collectFonts(elem[layout]);
    });

    this._applyColors(elem, BUTTON_COLOR_PROPS);

    elem.sync = Array.from(new Set([...sync]));

    return elem;
  }

  _applyColorsToStyles(/** Object **/ theme) {
    let styles = _.get(theme, "styles", {});

    _.forEach(
      styles,
      _.bind(function (style, id) {
        this._applyColors(style, STYLE_LAYOUT_COLOR_PROPS);
      }, this)
    );

    return theme;
  }

  _applyColors(/** Object **/ item, /** Array **/ colorProps) {
    _.forEach(
      LAYOUTS,
      _.bind(function (layout) {
        _.forEach(
          colorProps,
          _.bind(function (prop) {
            const path = layout + "." + prop;
            let color = _.get(item, path);

            if (color) {
              color = this._getColor(color);
              this._applyValue(item, path, color);
            }
          }, this)
        );
      }, this)
    );

    return item;
  }

  _applyValue(/** Object **/ item, /** String **/ path, value, required) {
    let apply = true;

    if (required) {
      apply = _.has(item, path);
    }

    if (apply && !_.isEmpty(value)) {
      _.set(item, path, value);
    }

    return item;
  }

  _getColor(color) {
    color = color.replace("-", ".");
    const parts = _.split(color, ":");
    color = _.get(this.theme, parts[0], parts[0]);
    if (parts.length == 2) {
      color = color + ":" + parts[1];
    }

    return color;
  }
}

export default ThemeApplier;
export {
  LAYOUTS,
  STYLE_LAYOUT_COLOR_PROPS,
  ELEMENT_COLOR_PROPS,
  BACKGROUND_COLOR_PROPS,
  TEXT_STYLE_PROPS,
};
